import React, { useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useWindowSize } from 'usehooks-ts';

import Constants from '../config.js';
import { UserContext } from '../Contexts/UserContext/UserContext.js';
import { isTokenScopedPathname } from '../helpers/useTokenOrigin.js';


/**
 * MobileSectionNavButtons — injected at the bottom of every token/BSV page
 * (and admin sub-page) on mobile only.
 *
 * Renders, as a row of buttons, all the *other* items that share the current
 * menu section (i.e. the same section the active page belongs to). The
 * current page is excluded so the user can jump to any sibling page without
 * opening the full menu.
 *
 *   - Token-scoped (non-admin) page  -> the token section items
 *       (Balance / Mintuples [non-BSV] / History / Send / Buy & Sell)
 *   - Admin sub-page                  -> the admin section items
 *       (Mint / Mintuples / Global Burn / Burn / Revoke Backup / Create Contracts)
 *
 * Renders null on desktop and on non-token-scoped routes (token picker home
 * and Settings), which have their own dedicated mobile controls.
 */
const MobileSectionNavButtons = () => {
	const location = useLocation();
	const history = useHistory();
	const userContext = useContext(UserContext);
	const { width: windowWidth = 0 } = useWindowSize();
	const isMobile = windowWidth < Constants.WINDOW_WIDTH_THRESHOLD_PX;

	if (!isMobile) return null;

	const pathname = location.pathname;
	if (!isTokenScopedPathname(pathname)) return null;

	// @ts-ignore — get_selected_coin_props is a runtime helper on UserContext
	const coinProps = userContext.get_selected_coin_props();
	const tokenid = coinProps ? coinProps.tokenid : null;
	if (!tokenid) return null;

	const prefix = '/wallet/' + tokenid;
	const isAdmin = pathname.indexOf('/admin/') !== -1;

	let items;
	if (isAdmin) {
		const adminPrefix = prefix + '/admin';
		items = [
			{ key: adminPrefix + '/mint', label: 'Mint' },
			{ key: adminPrefix + '/mintuples', label: 'Mintuples' },
			{ key: adminPrefix + '/global-burn', label: 'Global Burn' },
			{ key: adminPrefix + '/burn', label: 'Burn' },
			{ key: adminPrefix + '/revoke-backup', label: 'Revoke Backup' },
			{ key: adminPrefix + '/create-contracts', label: 'Create Contracts' },
		];
	} else {
		items = [{ key: prefix, label: 'Balance' }];
		if (tokenid !== 'bsv') {
			items.push({ key: prefix + '/mintuples', label: 'Mintuples' });
		}
		items.push(
			{ key: prefix + '/history', label: 'History' },
			{ key: prefix + '/send', label: 'Send' },
			{ key: prefix + '/swap', label: 'Buy & Sell' },
		);
	}

	// /wallet/<id>/balance is an alias for /wallet/<id> (the Balance entry's key)
	const currentKey = pathname === prefix + '/balance' ? prefix : pathname;
	const others = items.filter(it => it.key !== currentKey);
	if (others.length === 0) return null;

	return (
		<div className="mobileSectionNavButtons">
			{others.map(it => (
				<button
					key={it.key}
					type="button"
					className="mobileSectionNavButton"
					onClick={() => history.push(it.key)}
				>
					{it.label}
				</button>
			))}
		</div>
	);
};

export default MobileSectionNavButtons;
